import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, secureDelete, securePost } from "../../config/api";
import { useHistory } from "react-router-dom";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import OptionsMenu from "../../components/OptionsMenu";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";
import { PrimaryButton } from "../../components/PrimaryButton";

import EyeIcon from "../../icons/eye.svg";
import DeleteIcon from "../../icons/trashR.svg";
import modifyIcon from "../../icons/edit.svg";
import AddIcon from "../../icons/plusB.svg";

const STATUS_COLORS = {
  VALIDATED: "#06D6A0",
  UNDER_APPROVAL: "#FEA26F",
  CANCELLED: "#EF466F",
};

const Expenses = () => {
  const [, setExpensesData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    commercialId: null,
    dateRange: {},
    status: [],
    needle: "",
  });
  const [, setClients] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const history = useHistory();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const getStatusLabel = (status) => t(`expense_status.${status}`);

  const columns = [
    { label: t(`columns.paymentNumber`), key: "expenseNumber" },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: t(`columns.by`), key: "user", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: t(`columns.total`), key: "price", noJustifyEnd: true },
   {
      label: t("columns.status"),
      key: "status",
      render: (status, row) => {
        return (
          <span
            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center"
            style={{ backgroundColor: STATUS_COLORS[status] || "#A8ADC3" }}
          >
            {getStatusLabel(status).toUpperCase()}
          </span>
        );
      },
    },
    {
      key: "actions",
      render: (_, rowData) =>
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const getOptions = (item) => {
    const options = [
      { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) },
      item.status === 'UNDER_APPROVAL' && {
        label: t(`actions.modify`),
        icon: modifyIcon,
        action: () => handleModify(item),
      },
      item.status !== 'CANCELLED' && {
        label: t(`actions.delete`),
        color: '#EF466F',
        icon: DeleteIcon,
        action: () => handleDelete(item),
      },
    ];
    return options.filter(Boolean)
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
    },
    {
      name: "commercialId",
      label: t(`filters.orders.commercial`),
      type: "dropdown",
      options: [
        ...commercials.map((commercial) => ({
          value: commercial.id,
          label: `${commercial.firstName} ${commercial.lastName}`,
        })),
      ],
    },
    {
      name: "status",
      label: t(`filters.orders.status`),
      type: "checkbox-dropdown",
      options: [
        { value: "VALIDATED", label: t(`expense_status.VALIDATED`) },
        { value: "UNDER_APPROVAL", label: t(`expense_status.UNDER_APPROVAL`) },
        { value: "DECLINED", label: t(`expense_status.DECLINED`) },
        { value: "CANCELLED", label: t(`expense_status.CANCELLED`) },
      ],
    },
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const clientResponse = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientResponse.data);

        const commercialResponse = await secureGet("/users/ROLE_COMMERCIAL", showSnackbar, { signal });
        setCommercials(commercialResponse.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedExpenses = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);
    try {
      const requestBody = {
        commercialId: activeFilters.commercialId || null,
        startDate: activeFilters.startDate || null,
        endDate: activeFilters.endDate || null,
        status: activeFilters.status.length > 0 ? activeFilters.status : null,
        needle: activeFilters.needle || null,
      };


      const response = await securePost(`/expenses/all/paged?page=${page}&size=${size}`, requestBody, showSnackbar, { signal });

      const { content, totalPages } = response.data;
      setExpensesData(content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching expenses:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);

      fetchPaginatedExpenses(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchPaginatedExpenses(0, pageSize, filters);
    }
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedExpenses(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedExpenses(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else if (filterName === "status") {
      updatedFilters.status = filterValue;
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedExpenses(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      commercialId: null,
      dateRange: {},
      status: [],
      needle: "",
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedExpenses(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await secureDelete(`/expenses/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedExpenses(currentPage, pageSize);
      showSnackbar(t(`confirmation.expenseDelete`), 'success');
    } catch (error) {
      console.error("Error deleting expense:", error);
      setDeletePopupVisible(false);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/expenses/details/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  const handleModify = (item) => {
    history.push({
      pathname: `/expenses/modify/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  }

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.expenses")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.expenses`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_expense`)}
            icon={AddIcon}
            onClick={() => history.push("/expenses/add")}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <TableLoadingArea loading={loading}>
        <Table
          columns={columns}
          data={currentPageData}
          onRowClick={(row) => handleDetails(row)}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Expenses;